// Shared recurrence builder, lifted from slot-form-sheet.jsx so the session
// block editor can reuse the exact same weekly / monthly (day-of-month or
// Nth-weekday) pills plus the RDATE ("also include") and EXDATE ("also
// exclude") date lists. Controlled: the parent owns the value object and
// receives partial patches via onChange.
//
// Value shape (all keys optional-tolerant):
//   { frequency: "weekly"|"monthly"|"dates", monthlyVariant: "dayOfMonth"|"nthWeekday",
//     weekdays: number[], monthDays: number[], monthWeeks: number[],
//     repeatIntervalWeeks: number, repeatIntervalMonths: number,
//     dates: "YYYY-MM-DD"[], excludeDates: "YYYY-MM-DD"[] }
//
// slot-form-sheet.jsx still carries its private copy for now (kept untouched
// to contain risk); migrating it here is a follow-up.

import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { cn } from "@/lib/utils";
import { FieldLabel, dateToYmd } from "./form-fields";

export const WEEKDAY_LABELS = [
  { iso: 1, short: "Mo" },
  { iso: 2, short: "Tu" },
  { iso: 3, short: "We" },
  { iso: 4, short: "Th" },
  { iso: 5, short: "Fr" },
  { iso: 6, short: "Sa" },
  { iso: 7, short: "Su" },
];

// Week-of-month pills for the monthly "Nth weekday" variant. -1 is the sentinel
// for "last occurrence of that weekday in the month".
export const MONTH_WEEK_LABELS = [
  { value: 1, short: "1st" },
  { value: 2, short: "2nd" },
  { value: 3, short: "3rd" },
  { value: 4, short: "4th" },
  { value: -1, short: "Last" },
];

// 1..31 with a trailing "Last day of month" sentinel (-1).
const MONTH_DAY_VALUES = Array.from({ length: 31 }, (_, i) => i + 1);

// Sort month-day / month-week values so the "Last" sentinel (-1) lands at the
// end rather than the beginning.
export function sortMonthSentinelValues(values) {
  return [...(values || [])].sort((a, b) => {
    if (a === -1) return 1;
    if (b === -1) return -1;
    return a - b;
  });
}

export function PillButton({ active, onClick, children, type = "button", title }) {
  return (
    <button
      type={type}
      onClick={onClick}
      title={title}
      className={cn(
        "inline-flex h-8 min-w-[36px] items-center justify-center rounded-md border px-3 text-xs font-semibold transition-colors",
        active
          ? "border-primary bg-primary text-primary-foreground shadow-sm"
          : "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

/**
 * Multi-date picker for RDATE/EXDATE lists. Selecting a date appends it (the
 * parent dedupes + sorts); each selected date renders as a removable chip.
 */
export function DateListPicker({ dates, onAdd, onRemove }) {
  const [draft, setDraft] = useState(null);

  const handleAdd = () => {
    if (!(draft instanceof Date) || Number.isNaN(draft.getTime())) return;
    onAdd(draft);
    setDraft(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <DatePicker date={draft} setDate={setDraft} />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 shrink-0 [&_svg]:size-3.5"
          onClick={handleAdd}
          disabled={!(draft instanceof Date)}
        >
          <Plus />
          Add date
        </Button>
      </div>
      {dates.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No dates yet. Pick a date above and press <em>Add date</em>.
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {dates.map((ymd) => (
            <span
              key={ymd}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs font-mono tabular-nums"
            >
              {ymd}
              <button
                type="button"
                onClick={() => onRemove(ymd)}
                className="inline-flex size-4 items-center justify-center rounded text-muted-foreground/70 transition-colors hover:text-destructive"
                title={`Remove ${ymd}`}
                aria-label={`Remove ${ymd}`}
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Validation shared by the consumers; returns an errors object keyed like the
// slot-form-sheet's (weekdays / monthDays / monthWeeks / repeatInterval* / dates).
export function validateRecurrence(value, { allowDatesFrequency = true } = {}) {
  const errors = {};
  const frequency = value?.frequency || "weekly";
  if (frequency === "weekly") {
    if (!Array.isArray(value.weekdays) || value.weekdays.length === 0) {
      errors.weekdays = "Pick at least one weekday.";
    }
    const weeks = Number(value.repeatIntervalWeeks);
    if (!Number.isInteger(weeks) || weeks < 1) {
      errors.repeatIntervalWeeks = "Repeat interval must be ≥ 1.";
    }
  } else if (frequency === "monthly") {
    const months = Number(value.repeatIntervalMonths);
    if (!Number.isInteger(months) || months < 1) {
      errors.repeatIntervalMonths = "Repeat interval must be ≥ 1.";
    }
    if (value.monthlyVariant === "dayOfMonth") {
      if (!Array.isArray(value.monthDays) || value.monthDays.length === 0) {
        errors.monthDays = "Pick at least one day of the month.";
      }
    } else {
      if (!Array.isArray(value.monthWeeks) || value.monthWeeks.length === 0) {
        errors.monthWeeks = "Pick at least one week of the month.";
      }
      if (!Array.isArray(value.weekdays) || value.weekdays.length === 0) {
        errors.weekdays = "Pick at least one weekday.";
      }
    }
  } else if (frequency === "dates") {
    if (!allowDatesFrequency) {
      errors.dates = "Explicit date lists aren't available here.";
    } else if (!Array.isArray(value.dates) || value.dates.length === 0) {
      errors.dates = "Add at least one date.";
    }
  }
  return errors;
}

function toggleInList(list, entry) {
  const current = Array.isArray(list) ? list : [];
  return current.includes(entry) ? current.filter((v) => v !== entry) : [...current, entry];
}

function addYmd(list, date) {
  const ymd = dateToYmd(date);
  if (!ymd) return list;
  return Array.from(new Set([...(list || []), ymd])).sort();
}

function removeYmd(list, ymd) {
  return (list || []).filter((d) => d !== ymd);
}

/**
 * The recurrence field group. Frequency pills render only the variants the
 * consumer allows; the RDATE/EXDATE lists always render for weekly/monthly.
 */
export function RecurrenceFields({
  value,
  onChange,
  errors = {},
  allowDayOfMonth = true,
  allowDatesFrequency = true,
}) {
  const frequency = value?.frequency || "weekly";
  const monthlyVariant = allowDayOfMonth ? value?.monthlyVariant || "dayOfMonth" : "nthWeekday";
  const patch = (partial) => onChange?.(partial);

  const weekdayPills = (
    <div className="space-y-2">
      <FieldLabel>Weekdays</FieldLabel>
      <div className="flex flex-wrap gap-1.5">
        {WEEKDAY_LABELS.map((day) => (
          <PillButton
            key={day.iso}
            active={(value?.weekdays || []).includes(day.iso)}
            onClick={() => patch({ weekdays: toggleInList(value?.weekdays, day.iso).sort((a, b) => a - b) })}
            title={`ISO ${day.iso}`}
          >
            {day.short}
          </PillButton>
        ))}
      </div>
      {errors.weekdays ? <p className="text-xs text-destructive">{errors.weekdays}</p> : null}
    </div>
  );

  return (
    <>
      <div className="space-y-2">
        <FieldLabel>Frequency</FieldLabel>
        <div className="flex flex-wrap gap-2">
          <PillButton active={frequency === "weekly"} onClick={() => patch({ frequency: "weekly" })}>
            Weekly
          </PillButton>
          <PillButton active={frequency === "monthly"} onClick={() => patch({ frequency: "monthly" })}>
            Monthly
          </PillButton>
          {allowDatesFrequency ? (
            <PillButton
              active={frequency === "dates"}
              onClick={() => patch({ frequency: "dates" })}
              title="Repeat on an explicit list of calendar dates"
            >
              Specific dates
            </PillButton>
          ) : null}
        </div>
      </div>

      {frequency === "dates" && allowDatesFrequency ? (
        <div className="space-y-2">
          <FieldLabel>Dates</FieldLabel>
          <DateListPicker
            dates={value?.dates || []}
            onAdd={(date) => patch({ dates: addYmd(value?.dates, date) })}
            onRemove={(ymd) => patch({ dates: removeYmd(value?.dates, ymd) })}
          />
          {errors.dates ? <p className="text-xs text-destructive">{errors.dates}</p> : null}
        </div>
      ) : frequency === "weekly" ? (
        <>
          {weekdayPills}
          <div className="space-y-2">
            <FieldLabel>Repeat every</FieldLabel>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                max={52}
                value={value?.repeatIntervalWeeks ?? 1}
                onChange={(e) => patch({ repeatIntervalWeeks: Math.max(1, Number(e.target.value) || 1) })}
                className="h-9 w-24"
              />
              <span className="text-sm text-muted-foreground">week(s)</span>
            </div>
            {errors.repeatIntervalWeeks ? (
              <p className="text-xs text-destructive">{errors.repeatIntervalWeeks}</p>
            ) : null}
          </div>
        </>
      ) : (
        <>
          {allowDayOfMonth ? (
            <div className="space-y-2">
              <FieldLabel>Monthly pattern</FieldLabel>
              <div className="flex gap-2">
                <PillButton
                  active={monthlyVariant === "dayOfMonth"}
                  onClick={() => patch({ monthlyVariant: "dayOfMonth" })}
                  title="e.g. 1st and 15th of every month"
                >
                  By day of month
                </PillButton>
                <PillButton
                  active={monthlyVariant === "nthWeekday"}
                  onClick={() => patch({ monthlyVariant: "nthWeekday" })}
                  title="e.g. 2nd Tuesday of every month"
                >
                  By Nth week
                </PillButton>
              </div>
            </div>
          ) : null}

          {monthlyVariant === "dayOfMonth" && allowDayOfMonth ? (
            <div className="space-y-2">
              <FieldLabel>Days of the month</FieldLabel>
              <div className="flex flex-wrap gap-1.5">
                {MONTH_DAY_VALUES.map((day) => (
                  <PillButton
                    key={day}
                    active={(value?.monthDays || []).includes(day)}
                    onClick={() => patch({ monthDays: sortMonthSentinelValues(toggleInList(value?.monthDays, day)) })}
                    title={`Day ${day}`}
                  >
                    {day}
                  </PillButton>
                ))}
                <PillButton
                  active={(value?.monthDays || []).includes(-1)}
                  onClick={() => patch({ monthDays: sortMonthSentinelValues(toggleInList(value?.monthDays, -1)) })}
                  title="Last day of the month (28/29/30/31)"
                >
                  Last
                </PillButton>
              </div>
              {errors.monthDays ? <p className="text-xs text-destructive">{errors.monthDays}</p> : null}
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <FieldLabel>Week of the month</FieldLabel>
                <div className="flex flex-wrap gap-1.5">
                  {MONTH_WEEK_LABELS.map((week) => (
                    <PillButton
                      key={week.value}
                      active={(value?.monthWeeks || []).includes(week.value)}
                      onClick={() =>
                        patch({ monthWeeks: sortMonthSentinelValues(toggleInList(value?.monthWeeks, week.value)) })
                      }
                      title={
                        week.value === -1
                          ? "Last occurrence of that weekday in the month"
                          : `${week.short} occurrence in the month`
                      }
                    >
                      {week.short}
                    </PillButton>
                  ))}
                </div>
                {errors.monthWeeks ? <p className="text-xs text-destructive">{errors.monthWeeks}</p> : null}
              </div>
              {weekdayPills}
            </>
          )}

          <div className="space-y-2">
            <FieldLabel>Repeat every</FieldLabel>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                max={24}
                value={value?.repeatIntervalMonths ?? 1}
                onChange={(e) => patch({ repeatIntervalMonths: Math.max(1, Number(e.target.value) || 1) })}
                className="h-9 w-24"
              />
              <span className="text-sm text-muted-foreground">month(s)</span>
            </div>
            {errors.repeatIntervalMonths ? (
              <p className="text-xs text-destructive">{errors.repeatIntervalMonths}</p>
            ) : null}
          </div>
        </>
      )}

      {frequency !== "dates" ? (
        <>
          <div className="space-y-2">
            <FieldLabel>Also include specific dates (optional)</FieldLabel>
            <p className="text-xs text-muted-foreground/80">
              Extra one-off dates added on top of the {frequency === "monthly" ? "monthly" : "weekly"} pattern.
              Duplicates that already fall on the pattern are ignored.
            </p>
            <DateListPicker
              dates={value?.dates || []}
              onAdd={(date) => patch({ dates: addYmd(value?.dates, date) })}
              onRemove={(ymd) => patch({ dates: removeYmd(value?.dates, ymd) })}
            />
            {errors.dates ? <p className="text-xs text-destructive">{errors.dates}</p> : null}
          </div>

          <div className="space-y-2">
            <FieldLabel>Also exclude specific dates (optional)</FieldLabel>
            <p className="text-xs text-muted-foreground/80">
              One-off dates removed from the {frequency === "monthly" ? "monthly" : "weekly"} pattern. Overrides any
              matching included or pattern date.
            </p>
            <DateListPicker
              dates={value?.excludeDates || []}
              onAdd={(date) => patch({ excludeDates: addYmd(value?.excludeDates, date) })}
              onRemove={(ymd) => patch({ excludeDates: removeYmd(value?.excludeDates, ymd) })}
            />
          </div>
        </>
      ) : null}
    </>
  );
}
